export type CurriculumBackLabelKey = "curriculum.back.toCurriculum" | "curriculum.back.toLesson";

export type CurriculumBackNav = {
  href: string;
  labelKey: CurriculumBackLabelKey;
};

type SearchParamsLike = {
  get(name: string): string | null;
};

function buildLessonHref(
  subjectId: string,
  chapterId: string,
  unitId: string,
  lessonId: string,
  searchParams?: SearchParamsLike | null,
): string {
  const base = `/curriculum/${subjectId}/${chapterId}/${unitId}/${lessonId}`;
  const content = searchParams?.get("content");
  const version = searchParams?.get("version");

  if (content) return `${base}?content=${encodeURIComponent(content)}`;
  if (version) return `${base}?version=${encodeURIComponent(version)}`;
  return base;
}

/**
 * Tree retirement (plan 18-10, SC1): the browse tree is no longer part of
 * primary navigation, so every browse-level back target collapses to the
 * /curriculum board. Only lesson-runner sub-pages (flashcards/homework under
 * a lesson) keep their back target to the lesson page - with content/version
 * param passthrough intact so review-mode deep links survive.
 */
export function getCurriculumBackNav(
  pathname: string,
  searchParams?: SearchParamsLike | null,
): CurriculumBackNav | null {
  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] !== "curriculum") return null;

  const depth = parts.length - 1;
  if (depth <= 0) return null;

  const subjectId = parts[1];
  const chapterId = parts[2];
  const unitId = parts[3];
  const fourth = parts[4];
  const fifth = parts[5];

  // The lesson archive lives beside the board.
  if (subjectId === "history") {
    return { href: "/curriculum", labelKey: "curriculum.back.toCurriculum" };
  }

  if (depth === 5 && (fifth === "flashcards" || fifth === "homework")) {
    return {
      href: buildLessonHref(subjectId, chapterId, unitId, fourth, searchParams),
      labelKey: "curriculum.back.toLesson",
    };
  }

  if (depth >= 5) {
    return null;
  }

  // Depths 1-4 (retired subject/chapter/unit list pages and the lesson
  // runner itself) all return to the board.
  return { href: "/curriculum", labelKey: "curriculum.back.toCurriculum" };
}
